/**
 * KIAN-Aktionen: Entwürfe, die KIAN im Chat vorbereitet (Tickets anlegen/ändern, Import aus
 * Dateien) und die der Techniker in der Karte prüft und bestätigt. Gegenstück zu
 * backend/src/routes/kianActions.ts.
 */

import type { RequestFn } from '../http';

export type KianActionKind = 'TICKET_CREATE' | 'TICKET_UPDATE' | 'KNOWLEDGE_ARTICLE';
export type KianActionSource = 'CHAT' | 'FILE_IMPORT';
export type KianActionStatus = 'PROPOSED' | 'RUNNING' | 'DONE' | 'PARTIAL' | 'FAILED' | 'DISCARDED';
export type KianActionItemStatus = 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED' | 'SKIPPED' | 'UNKNOWN' | 'QUEUED';

/** Stand eines geparkten Eintrags in den weclapp-Wartelisten (nur bei QUEUED) */
export interface KianQueueState {
  state: 'WAITING' | 'SYNCED' | 'STUCK';
  edits: number;
  comments: number;
  /** Die Neuanlage selbst wartet — das Ticket steht erst im ServiceHub */
  ticket?: boolean;
}
export type KianValueMapField = 'customer' | 'status' | 'priority' | 'category' | 'assignee';

export interface KianDraftComment {
  text: string;
  internal?: boolean | null;
  date?: string | null;
  author?: string | null;
}

export interface KianDraftBlocker {
  waitingFor: string;
  since?: string | null;
  note?: string | null;
  followUp?: string | null;
}

/** Entwurf eines Tickets — Text (wie genannt) und ID (ausdrücklich gewählt); die ID gewinnt. */
export interface KianTicketDraft {
  ticket?: string | null;
  customerId?: string | null;
  customer?: string | null;
  contactId?: string | null;
  contact?: string | null;
  subject?: string | null;
  description?: string | null;
  statusId?: string | null;
  status?: string | null;
  priorityId?: string | null;
  priority?: string | null;
  categoryId?: string | null;
  category?: string | null;
  assigneeId?: string | null;
  assignee?: string | null;
  dueDate?: string | null;
  blocker?: KianDraftBlocker | null;
  clearBlocker?: boolean | null;
  comments?: KianDraftComment[] | null;
  assignReason?: string | null;
  unassign?: boolean | null;
  externalRef?: string | null;
  sourceCreatedAt?: string | null;
  extraFields?: Array<{ label: string; value: string }> | null;
}

/** Anleitung für die Wissensdatenbank (KNOWLEDGE_ARTICLE) — Gegenstück zu knowledgeArticleTypes.ts */
export interface KianKnowledgeDraft {
  title: string;
  content: string;
  category: string;
  tags: string[];
  /** ALL = für alle Kunden, CUSTOMERS = nur für `customerIds` */
  scope: 'ALL' | 'CUSTOMERS';
  customerIds: string[];
  /** Genannte, noch nicht eindeutig zugeordnete Kunden */
  customerRefs: string[];
  revision: number;
  revisedBy: 'KIAN' | 'USER';
  changeNote: string | null;
}

export interface KianKnowledgeResolved {
  customers: Array<{ id: string; name: string; number: string | null }>;
  similar: Array<{ id: string; title: string; slug: string; status: string }>;
  articleId?: string | null;
  articleSlug?: string | null;
}

export interface KianResolvedRef {
  id: string;
  name: string;
}

export interface KianResolvedDraft {
  ticket?: {
    id: string;
    number: string | null;
    subject: string | null;
    partyId: string | null;
    partyName: string | null;
    statusId: string | null;
    statusName: string | null;
    statusInternal: string | null;
    assigneeId: string | null;
    assigneeName: string | null;
  } | null;
  customer?: (KianResolvedRef & { number?: string | null }) | null;
  contact?: KianResolvedRef | null;
  status?: (KianResolvedRef & { internal: string | null }) | null;
  priority?: KianResolvedRef | null;
  category?: KianResolvedRef | null;
  assignee?: (KianResolvedRef & { portalUserId: string | null; isSelf: boolean }) | null;
  assigneeAddedForStatus?: boolean;
  unassign?: boolean;
  dueDate?: string | null;
  comments?: Array<{ text: string; internal: boolean }>;
  subject?: string | null;
  description?: string | null;
}

export interface KianIssueCandidate {
  id: string;
  label: string;
  hint?: string | null;
}

export interface KianDraftIssue {
  field: string;
  level: 'error' | 'warning' | 'info';
  message: string;
  candidates?: KianIssueCandidate[];
  /** Wertzuordnung, aus der das Problem stammt — Auswahl gilt dann für alle gleichen Quelltexte */
  mapKey?: string;
}

export interface KianValueMapEntry {
  field: KianValueMapField;
  key: string;
  source: string;
  count: number;
  targetId: string | null;
  targetName: string | null;
  origin: 'auto' | 'manual' | 'none';
  candidates?: KianIssueCandidate[];
}

export interface KianActionItem {
  id: string;
  position: number;
  include: boolean;
  status: KianActionItemStatus;
  step: string | null;
  label: string;
  ready: boolean;
  draft?: KianTicketDraft;
  resolved?: KianResolvedDraft | null;
  /** Nur Anleitungen (KNOWLEDGE_ARTICLE) */
  article?: KianKnowledgeDraft;
  articleResolved?: KianKnowledgeResolved | null;
  issues: KianDraftIssue[];
  ticketId: string | null;
  ticketNumber: string | null;
  commentsDone: number;
  commentsTotal: number;
  error: string | null;
  warnings: string[];
  attempts: number;
  /** Nur bei QUEUED: was aus den Wartelisten geworden ist */
  queue?: KianQueueState | null;
}

export interface KianActionCounts {
  total: number;
  included: number;
  ready: number;
  blocked: number;
  done: number;
  /** liegt in den weclapp-Wartelisten und geht automatisch nach */
  queued: number;
  failed: number;
  unknown: number;
  running: number;
}

export interface KianUsageSummary {
  models: string[];
  calls: number;
  tokensIn: number;
  tokensOut: number;
  costEur: number | null;
  costEstimated: boolean;
}

export interface KianAction {
  id: string;
  kind: KianActionKind;
  source: KianActionSource;
  status: KianActionStatus;
  title: string;
  sessionId: string | null;
  messageId: string | null;
  sourceFilename: string | null;
  options: { notifyCustomer: boolean; commentsInternal: boolean };
  valueMap: KianValueMapEntry[];
  notes: {
    import?: {
      filename: string;
      format: string;
      records: number;
      mapping: 'deterministic' | 'llm' | 'llm_text';
      fieldMap?: Array<{ field: string; source: string }>;
      skipped?: number;
      messages?: string[];
    };
    llmUsage?: KianUsageSummary | null;
  };
  counts: KianActionCounts;
  createdAt: string;
  updatedAt: string;
  confirmedAt: string | null;
  finishedAt: string | null;
  items: KianActionItem[];
}

export interface KianActionPatch {
  options?: { notifyCustomer?: boolean; commentsInternal?: boolean };
  valueMaps?: Array<{ field: KianValueMapField; key: string; targetId: string | null }>;
  items?: Array<{ id: string; include?: boolean; draft?: KianTicketDraft | Partial<KianKnowledgeDraft> }>;
}

export type KianContextType = 'customer' | 'project' | 'weclapp-ticket';

export interface KianContextCard {
  type: KianContextType;
  id: string;
  title: string;
  subtitle?: string | null;
  href?: string | null;
  facts: Array<{ label: string; value: string }>;
  lists: Array<{
    title: string;
    total?: number;
    items: Array<{ label: string; sublabel?: string | null; href?: string | null; tone?: 'default' | 'warning' | 'danger' | 'success' }>;
  }>;
  defaults: Record<string, string | null | undefined>;
}

export interface KianContextSearchItem {
  type: KianContextType;
  id: string;
  label: string;
  sublabel: string | null;
}

export function createKianActionsApi(request: RequestFn) {
  return {
    async getKianAction(id: string, opts?: { detail?: boolean }) {
      const q = opts?.detail === false ? '?detail=0' : '';
      return request<{ action: KianAction }>(`/kian/actions/${id}${q}`);
    },
    async listKianActions(sessionId: string) {
      return request<{ actions: KianAction[] }>(`/kian/actions?sessionId=${encodeURIComponent(sessionId)}`);
    },
    async updateKianAction(id: string, patch: KianActionPatch) {
      return request<{ action: KianAction }>(`/kian/actions/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
    },
    async executeKianAction(id: string, body?: { itemIds?: string[]; confirmUnknown?: boolean }) {
      return request<{ action: KianAction }>(`/kian/actions/${id}/execute`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      });
    },
    async discardKianAction(id: string) {
      return request<{ action: KianAction }>(`/kian/actions/${id}/discard`, { method: 'POST' });
    },
    async getKianContextCard(type: KianContextType, id: string) {
      return request<{ card: KianContextCard }>(`/kian/context/${type}/${encodeURIComponent(id)}`);
    },
    async searchKianContext(type: KianContextType, q: string) {
      const p = new URLSearchParams({ type, q });
      return request<{ items: KianContextSearchItem[] }>(`/kian/context-search?${p.toString()}`);
    },
  };
}
